import React from 'react';
import AceEditor from 'react-ace';
import { Ace } from 'ace-builds';
import langTools from 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-chrome';
import 'ace-builds/webpack-resolver';
import SchemaTable from '@/util/SchemaTable';
import { formatJSON } from '@/util/helpers/json-helpers';

let cachedCompletions: Record<string, Ace.Completion[]> = {}; // hash mapping first letter to list of items

const resetCachedCompletions = () => {
  cachedCompletions = {};
};

type Meta = 'schema' | 'column' | 'table';
type Hash = Record<string, Record<string, Meta[]>>;

//
// builds hash {letter -> { name -> [ meta, ... ] }}}
//
const cacheItem = (hash: Hash, name: string, meta: Meta) => {
  name = name.trim();
  const firstLetter = name.charAt(0).toLowerCase();
  let innerHash = hash[firstLetter];
  if (!innerHash) {
    innerHash = {};
    hash[firstLetter] = innerHash;
  }
  let innerArray = innerHash[name];
  if (!innerArray) {
    innerArray = [];
    innerHash[name] = innerArray;
  }
  innerArray.push(meta);
};

const EDITOR_STYLES = {
  marginBottom: 0,
  width: 'auto',
  border: '1px solid #ccc',
  borderRadius: '4px',
};

const useAddCompleter = () =>
  React.useEffect(() => {
    langTools.addCompleter({
      getCompletions(
        editor: AceEditor,
        session: Ace.EditSession,
        _pos: Ace.Position,
        prefix: string,
        callback: Ace.CompleterCallback,
      ) {
        if (prefix.length === 0) {
          callback(null, []);
          return;
        }
        const items = cachedCompletions[prefix.charAt(0).toLowerCase()];
        callback(null, items);
      },
    });
  }, []);

export type TrevorAceEditorProps = {
  initialSql?: string;
  onChange?: F<string>;
  readOnly: boolean;
  maxLines?: number;
  autoFocus?: boolean;
  isJSON?: boolean;
  initialJSON?: string;
  schema?: Maybe<SchemaTable[]>;
};

const TrevorAceEditor: React.FC<TrevorAceEditorProps> = ({
  initialSql,
  onChange,
  readOnly,
  maxLines = 22,
  autoFocus = true,
  isJSON = false,
  initialJSON,
  schema,
}) => {
  const editorRef = React.useRef<AceEditor>(null);
  const prevSchemaRef = React.useRef(schema);

  useAddCompleter();

  React.useEffect(() => {
    setTimeout(() => {
      editorRef.current?.editor.focus();
      // once navigator is visible, we need to resize the editor (or horizontal scroll might not work)
      editorRef.current?.editor.resize();
    }, 500);

    return resetCachedCompletions;
  }, []);

  React.useEffect(() => {
    if (schema && !prevSchemaRef.current) {
      const hash: Hash = {}; // {letter -> { name -> [ TABLE/COLUMN ] }}}
      schema.map((t) => {
        cacheItem(hash, t.schema(), 'schema');
        cacheItem(hash, t.table(), 'table');
        t.columns().map((c) => {
          cacheItem(hash, c.name(), 'column');
        });
      });
      Object.keys(hash).forEach((firstLetter) => {
        const innerHash = hash[firstLetter];
        const items: Ace.Completion[] = [];
        Object.keys(innerHash).forEach((name) => {
          const metaCounts: Record<string, number> = {};
          innerHash[name].forEach((meta) => {
            if (!metaCounts[meta]) {
              metaCounts[meta] = 1;
            } else {
              metaCounts[meta] += 1;
            }
          });
          (Object.keys(metaCounts) as Meta[]).forEach((meta) => {
            const count = metaCounts[meta];
            items.push({
              name,
              value: name,
              score: count,
              meta,
            });
          });
        });
        cachedCompletions[firstLetter] = items;
      });
    } else {
      prevSchemaRef.current = schema;
    }
  }, [schema]);

  const value = isJSON ? formatJSON(initialJSON) : initialSql;

  return (
    <AceEditor
      ref={editorRef}
      theme="chrome"
      mode={isJSON ? 'json' : 'sql'}
      name={`ace-editor-pre-${Math.random().toFixed(4)}`}
      readOnly={readOnly}
      value={value}
      showPrintMargin={false}
      focus={autoFocus && !readOnly}
      enableBasicAutocompletion={!readOnly}
      enableLiveAutocompletion={!readOnly}
      maxLines={maxLines}
      minLines={maxLines}
      onChange={onChange}
      style={EDITOR_STYLES}
    />
  );
};

export default TrevorAceEditor;
